// Aba "Por vir" do Férias e Licenças (redesenho 27/10/2026).
// "Fora agora" no topo; depois os futuros agrupados pelo mês de início.

import { Box, Paper, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  RegistroAfast, situacao, dataRetorno, diasEntre, diaDaSemana, rotuloTipo,
  MESES, MESES_CURTOS, SEMANA_CURTA, fmtCurta, fmtSemLonga, fmtEmDias,
} from './afastamentosDatas';
import { coresTipo, ALERTA, LARANJA, AZUL_CLARO, VERDE_ESCURO, CARTAO_SX } from './visual';

interface Props<T extends RegistroAfast> {
  registros: T[];                       // já com os filtros da tela
  hoje: string;
  alertas: Record<number, string[]>;    // calculados com a lista completa
  onEditar: (r: T) => void;
  onExcluir: (r: T) => void;
  onAbrirAgente?: (agenteId: number) => void;   // abre a Ficha do agente
}

function porInicio(a: RegistroAfast, b: RegistroAfast): number {
  if (a.data_inicio !== b.data_inicio) return a.data_inicio < b.data_inicio ? -1 : 1;
  return (a.agente_nome || '').localeCompare(b.agente_nome || '') || a.id - b.id;
}

const TITULO_GRUPO_SX = { fontSize: 14, fontWeight: 800, color: '#1B3A6B', letterSpacing: 0.8, mb: 1.2, mt: 1 };

export default function ListaPorVir<T extends RegistroAfast>({ registros, hoje, alertas, onEditar, onExcluir, onAbrirAgente }: Props<T>) {
  const agora = registros.filter(r => situacao(r, hoje) === 'agora').sort(porInicio);
  const futuros = registros.filter(r => situacao(r, hoje) === 'futuro').sort(porInicio);

  const grupos: Array<{ chave: string; titulo: string; itens: T[] }> = [];
  futuros.forEach(r => {
    const chave = r.data_inicio.slice(0, 7);
    let g = grupos.find(x => x.chave === chave);
    if (!g) {
      const [a, m] = chave.split('-').map(Number);
      g = { chave, titulo: `${MESES[m - 1].toUpperCase()} ${a}`, itens: [] };
      grupos.push(g);
    }
    g.itens.push(r);
  });

  if (!agora.length && !futuros.length) {
    return (
      <Paper elevation={0} sx={{ ...CARTAO_SX, p: 5, textAlign: 'center', color: '#6B7280', fontSize: 16 }}>
        Nenhum afastamento por vir com os filtros atuais.
      </Paper>
    );
  }

  const cartao = (r: T, modo: 'agora' | 'futuro') => (
    <CartaoAfastamento key={r.id} r={r} modo={modo} hoje={hoje} alertas={alertas[r.id] || []}
      onEditar={() => onEditar(r)} onExcluir={() => onExcluir(r)}
      onAbrirAgente={() => onAbrirAgente && onAbrirAgente(r.agente_id)} />
  );

  return (
    <Box>
      {agora.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ ...TITULO_GRUPO_SX, color: '#B45309' }}>FORA AGORA</Typography>
          {agora.map(r => cartao(r, 'agora'))}
        </Box>
      )}
      {futuros.length > 0 && (
        <Typography sx={{ fontSize: 14, color: '#6B7280', mb: 1 }}>
          Do mais próximo para o mais distante. Laranja = sai em até 15 dias.
        </Typography>
      )}
      {grupos.map(g => (
        <Box key={g.chave} sx={{ mb: 2.5 }}>
          <Typography sx={TITULO_GRUPO_SX}>{g.titulo}</Typography>
          {g.itens.map(r => cartao(r, 'futuro'))}
        </Box>
      ))}
    </Box>
  );
}

// ── Cartão de um afastamento ────────────────────────────────

interface CartaoProps {
  r: RegistroAfast;
  modo: 'agora' | 'futuro';
  hoje: string;
  alertas: string[];
  onEditar: () => void;
  onExcluir: () => void;
  onAbrirAgente: () => void;
}

function CartaoAfastamento({ r, modo, hoje, alertas, onEditar, onExcluir, onAbrirAgente }: CartaoProps) {
  const tipo = coresTipo(r.tipo);
  const volta = dataRetorno(r);
  const dias = diasEntre(r.data_inicio, r.data_fim) + 1;
  const [, mes, dia] = r.data_inicio.split('-').map(Number);

  const faltam = modo === 'agora' ? diasEntre(hoje, volta) : diasEntre(hoje, r.data_inicio);
  const perto = modo === 'futuro' && faltam <= 15;
  const seloCor = modo === 'agora' ? { bg: '#dcfce7', cor: '#14532d' } : perto ? LARANJA : AZUL_CLARO;
  const seloTxt = modo === 'agora' ? `volta ${fmtEmDias(faltam)}` : fmtEmDias(faltam);

  return (
    <Paper
      elevation={0}
      sx={{
        ...CARTAO_SX,
        mb: 1.5,
        p: 1.5,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: 2,
        ...(perto && { borderColor: '#fdba74' }),
      }}
    >
      {/* Bloco de data */}
      <Box sx={{
        width: 84, flexShrink: 0, borderRadius: 2, backgroundColor: AZUL_CLARO.bg, color: AZUL_CLARO.cor,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 1,
      }}>
        <Typography sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{dia}</Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase' }}>{MESES_CURTOS[mes - 1]}</Typography>
        <Typography sx={{ fontSize: 13 }}>{SEMANA_CURTA[diaDaSemana(r.data_inicio)]}</Typography>
      </Box>

      {/* Centro */}
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={onAbrirAgente}
            aria-label={`Abrir ficha de ${r.agente_nome}`}
            sx={{ all: 'unset', cursor: 'pointer', fontSize: 20, fontWeight: 700, color: '#111827', mr: 0.5,
              textDecoration: 'underline', textDecorationColor: 'transparent',
              '&:hover, &:focus-visible': { textDecorationColor: '#111827' } }}
          >
            {r.agente_nome}
          </Box>
          <Chip label={rotuloTipo(r.tipo)} size="small" sx={{ backgroundColor: tipo.bg, color: tipo.cor, fontWeight: 700 }} />
          {alertas.map(a => (
            <Chip key={a} size="small" icon={<WarningAmberIcon sx={{ color: `${ALERTA.cor} !important`, fontSize: '16px !important' }} />}
              label={a} sx={{ backgroundColor: ALERTA.bg, color: ALERTA.cor, fontWeight: 700 }} />
          ))}
          <Typography component="span" sx={{ fontSize: 12, color: '#9CA3AF' }}>
            {r.oficializado === false ? 'Não oficial' : 'Oficial'}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 15, color: '#374151', mt: 0.6 }}>
          {fmtCurta(r.data_inicio)} → {fmtCurta(r.data_fim)} · {dias} {dias === 1 ? 'dia' : 'dias'} ·{' '}
          <Box component="span" sx={{ color: VERDE_ESCURO, fontWeight: 700 }}>volta {fmtSemLonga(volta)}</Box>
        </Typography>
        {r.motivo && (
          <Box sx={{ mt: 1, p: 1.2, borderRadius: 2, backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6',
            fontSize: 14, color: '#4B5563', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {r.motivo}
          </Box>
        )}
      </Box>

      {/* Direita */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
        <Box sx={{ px: 1.5, py: 0.8, borderRadius: 2, backgroundColor: seloCor.bg, color: seloCor.cor,
          fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>
          {seloTxt}
        </Box>
        <Tooltip title="Editar" arrow>
          <IconButton aria-label={`Editar afastamento de ${r.agente_nome}`} onClick={onEditar} sx={{ width: 44, height: 44 }}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir" arrow>
          <IconButton aria-label={`Excluir afastamento de ${r.agente_nome}`} onClick={onExcluir} sx={{ width: 44, height: 44 }}>
            <DeleteIcon sx={{ color: '#EF4444' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}
